import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { ReferenceWithAnswers, ReferenceAnswer, referencesApi } from '../../api/references';
import { Question, questionsApi } from '../../api/questions';
import { useLanguage } from '../../contexts/LanguageContext';
import useEscapeKey from '../../hooks/useEscapeKey';

interface ReferenceAnswersModalProps {
  reference: ReferenceWithAnswers;
  jobId: string;
  isOpen: boolean;
  onClose: () => void;
}

const ReferenceAnswersModal: React.FC<ReferenceAnswersModalProps> = ({
  reference: initialReference,
  jobId,
  isOpen,
  onClose
}) => {
  const { t } = useLanguage();
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [completeReference, setCompleteReference] = useState<ReferenceWithAnswers | null>(null);
  
  // Close modal when Escape key is pressed
  useEscapeKey(onClose, isOpen);

  // Create a map of questionId -> Question for easier lookup
  const [questionMap, setQuestionMap] = useState<Record<string, Question>>({});

  // Fetch complete reference data with answers
  useEffect(() => {
    const fetchReferenceWithAnswers = async () => {
      if (!isOpen) return;
      
      try {
        // Fetch the full reference with answers
        const fetchedReference = await referencesApi.getById(initialReference.id);
        setCompleteReference(fetchedReference);
      } catch (err) {
        console.error('Failed to fetch reference details:', err);
        setError(t('candidates.referenceModals.referenceAnswersModal.error'));
      }
    };

    fetchReferenceWithAnswers();
  }, [initialReference.id, isOpen, t]);

  // Fetch questions for the job
  useEffect(() => {
    const fetchQuestions = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      
      try {
        const questionsData = await questionsApi.getByJobId(jobId);
        setQuestions(questionsData);
        
        // Create a map for quick lookup of questions by ID
        const qMap: Record<string, Question> = {};
        questionsData.forEach(question => {
          qMap[question.id] = question;
        });
        setQuestionMap(qMap);
      } catch (err) {
        console.error('Failed to fetch questions:', err);
        setError(prev => prev || t('candidates.referenceModals.referenceAnswersModal.error'));
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [jobId, isOpen, t]);

  // Group answers by question group
  const groupedAnswers = React.useMemo(() => {
    const grouped: Record<string, { question: Question; answer: ReferenceAnswer }[]> = {};
    
    // Only process answers if we have the complete reference data
    if (completeReference?.answers) {
      completeReference.answers.forEach(answer => {
        const question = questionMap[answer.questionId];
        if (!question) return;
        
        if (!grouped[question.group]) {
          grouped[question.group] = [];
        }
        
        grouped[question.group].push({ question, answer });
      });
    }
    
    return grouped;
  }, [completeReference?.answers, questionMap]);

  if (!isOpen) return null;

  const referenceName = completeReference?.full_name || initialReference.full_name;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('candidates.referenceModals.referenceAnswersModal.title', { name: referenceName })}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        
        {/* Reference information */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceAnswersModal.referenceInfo.company')}</p>
              <p className="font-medium">{completeReference?.company || initialReference.company}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceAnswersModal.referenceInfo.relationship')}</p>
              <p className="font-medium">{t(`relationships.${completeReference?.relationship_type || initialReference.relationship_type}`)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceAnswersModal.referenceInfo.department')}</p>
              <p className="font-medium">{t(`departments.${completeReference?.department || initialReference.department}`)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('candidates.referenceModals.referenceAnswersModal.referenceInfo.yearsWorked')}</p>
              <p className="font-medium">{completeReference?.years_worked || initialReference.years_worked}</p>
            </div>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading || !completeReference ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
              <span className="ml-2 text-gray-500">
                {!completeReference 
                  ? t('candidates.referenceModals.referenceAnswersModal.loading.reference') 
                  : t('candidates.referenceModals.referenceAnswersModal.loading.questions')}
              </span>
            </div>
          ) : error ? (
            <div className="bg-red-50 text-red-600 p-4 rounded-lg">
              <p>{error}</p>
            </div>
          ) : (
            <div className="space-y-6 overflow-y-auto">
              {Object.entries(groupedAnswers).map(([group, items]) => (
                <div key={group} className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-50 px-4 py-3 border-b">
                    <h3 className="font-medium text-gray-700">{group}</h3>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {items.map(({ question, answer }) => (
                      <div key={question.id} className="px-4 py-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          {question.label}
                        </p>
                        <p className="text-sm text-gray-600">
                          {answer.textAnswer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              
              {Object.keys(groupedAnswers).length === 0 && completeReference && (
                <div className="text-center py-10">
                  <p className="text-gray-500">{t('candidates.referenceModals.referenceAnswersModal.noAnswers')}</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors text-gray-700 font-medium"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceAnswersModal;